import { ExternalLink } from 'lucide-react';
import { cn } from 'shared-ui';

import { what3WordsUrl } from '../utils/poiFeatureProperties';
import { What3WordsLogo } from './What3WordsLogo';

/**
 * Labelled bordered control row used by the POI details panel (and the history event
 * detail view for its what3words rows). The `poi-panel-control` class carries the
 * shared bordered styling.
 */
export function PoiPanelField({
    label,
    meta,
    controlClassName,
    children,
}: {
    label: string;
    meta?: string;
    /** Extra classes for the control box — e.g. the multi-line free-text variant. */
    controlClassName?: string;
    children: React.ReactNode;
}) {
    return (
        <div className="space-y-1">
            <p className="text-foreground text-sm font-medium">
                {label}
                {meta && <span className="text-muted-foreground font-normal"> {meta}</span>}
            </p>
            <div className={cn('poi-panel-control', controlClassName)}>{children}</div>
        </div>
    );
}

/**
 * A what3words row: logo, the address as an external map link (via the canonical
 * what3WordsUrl helper), and an em dash when no address is recorded.
 */
export function PoiWhat3WordsField({
    label,
    meta,
    value,
}: {
    label: string;
    meta?: string;
    value: string | null | undefined;
}) {
    const url = value ? what3WordsUrl(value) : null;
    return (
        <PoiPanelField label={label} meta={meta}>
            <What3WordsLogo className="size-5 shrink-0" />
            {url ? (
                <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary flex-1 text-sm underline underline-offset-2"
                >
                    {value}
                </a>
            ) : (
                <span className="text-muted-foreground flex-1 text-sm">—</span>
            )}
            <ExternalLink
                className={cn('text-primary ml-auto size-4 stroke-[1.5]', !url && 'opacity-30')}
                aria-hidden="true"
            />
        </PoiPanelField>
    );
}
